/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { FaPlay, FaTrash, FaArrowRight, FaArrowLeft, FaCheck, FaFileUpload, FaSpinner, FaChalkboardTeacher, FaClock, FaListUl } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { durationOptions, FormInputs } from '../../../types/session-forms.types';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firebase-firestore';
import 'firebase/storage';
import { Collections } from '../../../types/collection';
import api from '../../../libs/axiosInstance';
import { useUserContext } from '../../../contexts/user.context';
import { Toast } from 'primereact/toast';
import { interviewTypes } from '../../../pages/NewSessionTypeSelectionPage';
import { useDropzone } from 'react-dropzone';
import { captureException } from '@sentry/react';
// import { useFeatureAccess } from '../../../hooks/useFeatureAccess'; // Removed - using real-time validation instead
import { apiUsageService } from '../../../services/api-usage.service';
import { environmentPricingService } from '../../../services/environment-pricing.service';

interface Props {
  icon: React.ReactElement;
  type: string;
}

export interface DurationOption {
  label: string;
  value: number;
}

const NewWhiteBoardSessionForm = ({ icon }: Props) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const toast = useRef<Toast>(null);
  const { user, reloadUserData } = useUserContext();
  const navigate = useNavigate();
  // const featureAccess = useFeatureAccess(); // Removed - using real-time validation instead

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [isReviewing, setIsReviewing] = useState(false);
  const [interviewFormData, setInterviewFormData] = useState<FormInputs>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const {
    control,
    register,
    watch,
    formState: { errors, touchedFields },
    trigger,
  } = useForm<FormInputs>({
    defaultValues: {
      sessionType: null,
      title: '',
      topics: [],
      duration: null,
    },
    mode: 'onBlur', // Validate on blur for better UX
  });

  useEffect(() => {
    const loadData = async () => {
      setIsInitialLoading(true);
      try {
        await reloadUserData();
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadData();
  }, [reloadUserData]);

  /**
   * ------------------------------------------------
   * Dropzone: Limit file size to 1MB per file
   *   - Show toast if user plan disallows upload
   *   - Show toast if file is larger than 1MB
   * ------------------------------------------------
   */
  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      // Check if user's plan allows file uploading
      if (!user?.subscription.fileUpload) {
        toast.current?.show({
          severity: 'warn',
          summary: '📎 File Upload Not Available',
          detail: 'File uploads are available on Pro and Enterprise plans. Upgrade to upload documents and get personalized feedback!',
          life: 4000,
        });
        return;
      }

      // If plan allows, add files
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    },
    [user]
  );

  const onDropRejected = useCallback((fileRejections: any) => {
    fileRejections.forEach(({ file, errors }: { file: any; errors: any }) => {
      errors.forEach((err: any) => {
        if (err.code === 'file-too-large') {
          toast.current?.show({
            severity: 'warn',
            summary: '📁 File Too Large',
            detail: `${file.name} exceeds the 2MB limit. Please compress your file or choose a smaller one.`,
            life: 4000,
          });
        } else {
          // Handle other errors here
          toast.current?.show({
            severity: 'warn',
            summary: 'File Rejected',
            detail: `${file.name} could not be uploaded. ${err.message}`,
            life: 3000,
          });
        }
      });
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc', '.docx'],
    },
    maxSize: 2 * 1024 * 1024, // 2MB limit per file
  });

  /**
   * -------------------------------
   * Remove a file from our array
   * -------------------------------
   */
  const removeFile = (fileIndex: number) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== fileIndex));
  };

  /**
   * -------------------------------
   * Duration options
   * -------------------------------
   */
  const populateDurationOption = () => {
    // Get the current plan's interviewLengths from the pricing service
    // This ensures we always use the latest plan definition, not stored user data
    const planName = user?.subscription?.name;
    if (planName) {
      const currentPlan = environmentPricingService.getPlanByName(planName);
      if (currentPlan?.interviewLengths) {
        return currentPlan.interviewLengths.map((interviewLength: string) => ({
          value: interviewLength,
          label: `${interviewLength} Minutes`,
        }));
      }
    }
    return durationOptions;
  };


  /**
   * Actually upload files to Firebase Storage,
   * then create the session with references.
   */
  const submitData = async (data: FormInputs) => {
    try {
      setIsSubmitting(true);
      const userId = firebase.auth().currentUser?.uid;
      if (!userId || !type) return;

      // REAL-TIME VALIDATION: Always check fresh usage before creating session
      try {
        const realTimeCheck = await apiUsageService.canCreateInterviewSession();
        if (!realTimeCheck.allowed) {
          toast?.current?.show({
            severity: 'warn',
            summary: '🎯 Session Limit Reached',
            detail: realTimeCheck.message || `You've used ${realTimeCheck.used} of ${realTimeCheck.limit} interview sessions this month. Upgrade your plan to continue improving your interview skills!`,
            life: 5000,
          });
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Error checking session limits:', error);
        toast?.current?.show({
          severity: 'error',
          summary: '❌ Validation Failed',
          detail: 'Unable to verify your session limits. Please try again or contact support.',
          life: 5000,
        });
        setIsSubmitting(false);
        return;
      }

      // Create session list reference
      const sessionListRef = firebase
        .firestore()
        .collection(Collections.Sessions)
        .doc(userId)
        .collection('list');

      // Create a new Session doc ID
      const newSessionId = firebase
        .firestore()
        .collection(Collections.Sessions)
        .doc().id;

      /**
       * -------------------------------
       * Upload files now
       * -------------------------------
       */
      const storageRef = firebase.storage().ref();
      const fileUrls: string[] = [];

      // Upload each file
      for (const file of selectedFiles) {
        const fileRef = storageRef.child(`user-docs/${userId}/${Date.now()}-${file.name}`);
        await fileRef.put(file);
        const downloadURL = await fileRef.getDownloadURL();
        fileUrls.push(downloadURL);
      }

      /**
       * -------------------------------
       * Create the session doc
       * -------------------------------
       */
      await sessionListRef.doc(newSessionId).set({
        ...data,
        generatedQuestion: false,
        generatingQuestion: true,
        // State machine fields for idempotency
        generationState: 'pending',
        generationAttempts: 0,
        appActionText: 'Working off information provided',
        completed: false,
        interviewType: type,
        status: 'In Progress',
        score: 0,
        duration: data.duration,
        timeRemaining: parseInt(`${data.duration?.value}`) * 60,
        createdAt: new Date().toISOString(),
        // store the final list of uploaded file URLs
        fileSources: fileUrls,
      });

      // Usage is already incremented by the backend in /ai/generate-questions endpoint
      // Emit event to refresh usage dashboard after question generation starts

      // Kick off question generation
      setTimeout(async () => {
        try {
          await api.post('/ai/generate-questions', {
            sessionType: data.sessionType?.label,
            jobRole: data.jobRole?.label,
            difficulty: data.difficulty?.label,
            duration: data.duration?.value,
            topics: data.topics?.map((t) => t.label),
            timeRemaining: parseInt(`${data.duration?.value}`) * 60,
            userId,
            sessionId: newSessionId,
            interviewType: type,
            sessionTitle: data.title,
            // pass the file URLs to the backend
            fileSources: fileUrls,
          });
          // Emit event to refresh usage dashboard after successful generation request
          window.dispatchEvent(new CustomEvent('session-created'));
        } catch (error) {
          console.error('Error generating questions:', error);
          // Still emit event even if generation fails (usage was already incremented)
          window.dispatchEvent(new CustomEvent('session-created'));
        }
      }, 500);


      navigate(`/session-details/${newSessionId}`);
    } catch (error) {
      captureException(error);
      toast?.current?.show({
        severity: 'error',
        summary: '❌ Session Creation Failed',
        detail: 'Could not create your whiteboard session. Please check your connection and try again.',
        life: 4000,
      });
      setIsSubmitting(false);
    }
  };

  const getInterViewLabel = (type: string) => {
    return interviewTypes.find((it) => it.value === type)?.name;
  };

  const validateStep = async (step: number) => {
    let isValid = true;

    switch (step) {
      case 1:
        isValid = await trigger(['title']);
        break;
      case 2:
        isValid = await trigger(['duration']);
        break;
      default:
        break;
    }

    return isValid;
  };

  const nextStep = async () => {
    const isValid = await validateStep(currentStep);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 2));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const renderStepIndicator = () => {
    const totalSteps = 2;
    const steps = [
      { number: 1, label: 'Topic' },
      { number: 2, label: 'Duration & Details' },
    ];

    return (
      <div className="mb-6 sm:mb-8">
        {/* Progress bar */}
        <div className="mb-4 sm:mb-6">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all duration-300 ease-out"
              style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex justify-between relative">
          {/* Connecting line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10 hidden sm:block" />
          <div
            className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-green-500 to-cyan-500 transition-all duration-300 ease-out -z-10 hidden sm:block"
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
          />

          {steps.map((step) => {
            const isCompleted = step.number < currentStep;
            const isActive = step.number === currentStep;

            return (
              <div key={step.number} className="flex flex-col items-center flex-1">
                <div
                  className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm sm:text-base font-semibold transition-all duration-300 ${isCompleted
                    ? 'bg-green-500 text-white shadow-lg scale-110'
                    : isActive
                      ? 'bg-cyan-500 text-white shadow-lg ring-4 ring-cyan-200 scale-110'
                      : 'bg-gray-200 text-gray-500'
                    }`}
                >
                  {isCompleted ? <FaCheck className="text-sm sm:text-base" /> : step.number}
                </div>
                <span
                  className={`text-xs sm:text-sm mt-2 text-center px-1 font-medium ${isActive ? 'text-cyan-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * -------------------------------
   * REVIEW STEP RENDER
   * -------------------------------
   */
  if (isReviewing && interviewFormData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 py-4 sm:py-8 bg-gray-50">
        <Toast ref={toast} />
        <motion.div
          className="w-full max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-center text-gray-800">Whiteboard Challenge</h2>

          <motion.div
            className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center mb-4 sm:mb-5">
              <div className="p-2 sm:p-3 bg-cyan-100 rounded-full mr-3 sm:mr-4">
                <FaChalkboardTeacher className="text-cyan-600 text-lg sm:text-xl" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">Challenge Overview</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div className="bg-cyan-50 rounded-lg p-4">
                <div className="text-sm text-cyan-700 uppercase font-medium mb-1">Session Type</div>
                <div className="font-semibold text-gray-800 flex items-center">
                  {getInterViewLabel(type || '')}
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-blue-700 uppercase font-medium mb-1">Topic</div>
                <div className="font-semibold text-gray-800 flex items-center">
                  {interviewFormData.title}
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm text-purple-700 uppercase font-medium mb-1">Duration</div>
                <div className="font-semibold text-gray-800 flex items-center">
                  <FaClock className="mr-2 text-purple-500" />
                  {interviewFormData.duration?.label}
                </div>
              </div>

              {interviewFormData.topics && interviewFormData.topics.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4 md:col-span-2">
                  <div className="text-sm text-green-700 uppercase font-medium mb-2">Focus Points</div>
                  <div className="flex flex-wrap gap-2">
                    {interviewFormData.topics.map((topic, index) => (
                      <span key={index} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm inline-flex items-center">
                        <FaListUl className="mr-1 text-xs" />
                        {topic.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 mt-5">
              <div className="text-sm text-yellow-700 uppercase font-medium mb-1">What to Expect</div>
              <p className="text-gray-700">
                You'll be provided with an intuitive practice screen where you can you can explain your thoughts and illustrate
                either with drawings on the screen or integrating with well established boards like (Miro, Draw.io, etc).
              </p>
            </div>
          </motion.div>

          {selectedFiles.length > 0 && (
            <motion.div
              className="bg-white p-6 rounded-xl shadow-lg mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full mr-4">
                  <FaFileUpload className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Reference Materials</h3>
              </div>

              <div className="space-y-3">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <FaFileUpload className="text-blue-500 mr-3 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Action buttons */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <motion.button
              onClick={() => {
                // Finalize data before submitting
                const topicsData =
                  interviewFormData.topics?.map((topic) => ({
                    label: topic.label,
                    value: topic.value,
                  })) || [];
                const tempData = {
                  ...interviewFormData,
                  topics: topicsData,
                };
                submitData(tempData);
              }}
              disabled={isSubmitting}
              className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-base sm:text-lg font-bold rounded-xl hover:from-cyan-600 hover:to-blue-700 shadow-lg transition flex-1 flex items-center justify-center min-h-[44px] disabled:opacity-50"
              whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  <span>Creating Session...</span>
                </>
              ) : (
                <>
                  <FaPlay className="mr-2" />
                  <span>Start Challenge</span>
                </>
              )}
            </motion.button>

            <motion.button
              onClick={() => setIsReviewing(false)}
              disabled={isSubmitting}
              className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 text-gray-700 text-base sm:text-lg font-bold rounded-xl hover:bg-gray-100 transition flex items-center justify-center min-h-[44px] disabled:opacity-50"
              whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
            >
              <FaArrowLeft className="mr-2" />
              <span>Make Changes</span>
            </motion.button>
          </div>

          {/* Loading overlay for submission */}
          {isSubmitting && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-xl p-6 sm:p-8 max-w-sm w-full mx-4 shadow-2xl"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="flex flex-col items-center">
                  <FaSpinner className="animate-spin text-cyan-500 text-4xl mb-4" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Creating Your Session</h3>
                  <p className="text-sm text-gray-600 text-center">Please wait while we set everything up for you...</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  /**
   * -------------------------------
   * MAIN FORM RENDER
   * -------------------------------
   */

  // Initial loading skeleton
  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Toast ref={toast} />
        <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 py-4 sm:py-8">
          <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-xl w-full max-w-2xl">
            <div className="animate-pulse space-y-6">
              <div className="text-center mb-6">
                <div className="h-12 w-12 bg-gray-200 rounded-full mx-auto mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
              <div className="space-y-4">
                <div className="h-2 bg-gray-200 rounded"></div>
                <div className="flex justify-between">
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                  <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-12 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Toast ref={toast} />
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 py-4 sm:py-8">
        <motion.div
          className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-xl w-full max-w-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className="text-center mb-4 sm:mb-6"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{icon}</div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">Start a New Session</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Configure your whiteboard practice session
            </p>
          </motion.div>

          {renderStepIndicator()}

          {/* ----------------- The Form ----------------- */}
          <form className="space-y-6">
            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Session Title */}
                  <div>
                    <label
                      htmlFor="title-input"
                      className="block text-gray-700 mb-2 text-sm sm:text-base font-medium"
                    >
                      What topic are you practicing? <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        id="title-input"
                        {...register('title', {
                          required: 'Session topic is required',
                          minLength: {
                            value: 3,
                            message: 'Topic must be at least 3 characters',
                          },
                          maxLength: {
                            value: 100,
                            message: 'Topic must be less than 100 characters',
                          },
                        })}
                        onBlur={() => trigger('title')}
                        className={`w-full px-4 py-3 pr-10 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm sm:text-base ${errors.title
                          ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                          : touchedFields.title && !errors.title
                            ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                            : 'border-gray-300 focus:ring-cyan-500 focus:border-cyan-500'
                          }`}
                        placeholder="e.g., System design discussion"
                        aria-invalid={errors.title ? 'true' : 'false'}
                        aria-describedby={errors.title ? 'title-error' : 'title-help'}
                      />
                      {touchedFields.title && !errors.title && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <FaCheck className="text-green-500 text-lg" />
                        </div>
                      )}
                    </div>
                    {errors.title && (
                      <p id="title-error" className="text-red-500 text-sm mt-1.5 flex items-center" role="alert">
                        <span className="mr-1">⚠️</span>
                        {errors.title.message}
                      </p>
                    )}
                    {!errors.title && touchedFields.title && (
                      <p id="title-help" className="text-green-600 text-xs mt-1.5">
                        ✓ Looks good!
                      </p>
                    )}
                  </div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  {/* Duration */}
                  <div>
                    <label
                      htmlFor="duration-select"
                      className="block text-gray-700 mb-2 text-sm sm:text-base font-medium"
                    >
                      Session Duration <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      name="duration"
                      control={control}
                      rules={{ required: 'Session Duration is required' }}
                      render={({ field }) => (
                        <div className="relative">
                          <Select
                            {...field}
                            inputId="duration-select"
                            options={populateDurationOption() || durationOptions}
                            placeholder="Select Duration"
                            classNamePrefix="react-select"
                            onChange={(value) => {
                              field.onChange(value);
                              trigger('duration');
                            }}
                            onBlur={() => trigger('duration')}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                borderColor: errors.duration
                                  ? '#ef4444'
                                  : touchedFields.duration && !errors.duration
                                    ? '#10b981'
                                    : state.isFocused ? '#06b6d4' : '#d1d5db',
                                borderWidth: '2px',
                                boxShadow: state.isFocused
                                  ? errors.duration
                                    ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                                    : touchedFields.duration && !errors.duration
                                      ? '0 0 0 3px rgba(16, 185, 129, 0.1)'
                                      : '0 0 0 3px rgba(6, 182, 212, 0.1)'
                                  : 'none',
                                '&:hover': {
                                  borderColor: errors.duration ? '#ef4444' : '#06b6d4',
                                },
                              }),
                            }}
                          />
                          {touchedFields.duration && !errors.duration && field.value && (
                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                              <FaCheck className="text-green-500 text-lg" />
                            </div>
                          )}
                        </div>
                      )}
                    />
                    {errors.duration && (
                      <p id="duration-error" className="text-red-500 text-sm mt-1.5 flex items-center" role="alert">
                        <span className="mr-1">⚠️</span>
                        {errors.duration.message}
                      </p>
                    )}
                    {!errors.duration && touchedFields.duration && watch('duration') && (
                      <p id="duration-help" className="text-green-600 text-xs mt-1.5">
                        ✓ Duration selected
                      </p>
                    )}
                  </div>

                  {/* Topics */}
                  <div>
                    <label className="block text-gray-700 mb-1 text-sm font-medium">
                      Focus points (Optional)
                    </label>
                    <Controller
                      name="topics"
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          {...field}
                          isMulti
                          options={[]}
                          placeholder="Select or enter Topics"
                          classNamePrefix="react-select"
                          onChange={(value) => field.onChange(value)}
                        />
                      )}
                    />
                  </div>

                  {/* -------------- DRAG & DROP ZONE ------------- */}
                  <div>
                    <label className="block text-gray-700 mb-1 text-sm font-medium">
                      Document Sources (Optional)
                    </label>

                    <div
                      {...getRootProps()}
                      className={`border-2 border-dashed p-6 rounded-lg text-center cursor-pointer ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                        }`}
                    >
                      <input {...getInputProps()} />
                      {isDragActive ? (
                        <p className="text-blue-500">Drop the files here ...</p>
                      ) : (
                        <p className="text-gray-500">
                          Drag and drop files here, or click to select files<br />
                          <span className="text-xs">Each file must not be above 2MB</span>
                        </p>
                      )}
                    </div>

                    {/* Show selected files with remove button */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {selectedFiles.map((file, index) => (
                          <div
                            key={file.name + index}
                            className="flex items-center justify-between border p-3 rounded-lg bg-gray-50"
                          >
                            <div className="flex items-center">
                              <FaFileUpload className="mr-2 text-blue-500" />
                              <div>
                                <p className="font-medium">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-red-600 hover:text-red-800 flex items-center"
                            >
                              <FaTrash className="mr-1" />
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

            </AnimatePresence>

            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 pt-4 sm:pt-6 border-t">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center justify-center min-h-[44px] px-4 sm:px-6 py-2.5 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium text-sm sm:text-base"
                >
                  <FaArrowLeft className="mr-2" />
                  Back
                </button>
              )}
              {currentStep < 2 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className={`flex items-center justify-center min-h-[44px] px-6 sm:px-8 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-medium text-sm sm:text-base ${currentStep > 1 ? 'sm:ml-auto' : 'w-full sm:w-auto'}`}
                >
                  Next
                  <FaArrowRight className="ml-2" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={async () => {
                    const isValid = await validateStep(currentStep);
                    if (isValid) {
                      const formData = watch();
                      setInterviewFormData(formData);
                      setIsReviewing(true);
                    }
                  }}
                  className={`flex items-center justify-center min-h-[44px] px-6 sm:px-8 py-2.5 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-medium text-sm sm:text-base ${currentStep > 1 ? 'sm:ml-auto' : 'w-full sm:w-auto'}`}
                >
                  <FaPlay className="mr-2" />
                  Start Session
                </button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default NewWhiteBoardSessionForm;
